/**
 * Centralized chart theme tokens and builder functions.
 *
 * ALL chart styling lives here — AG-Charts themes and series defaults.
 * Widget renderers in items/ should never contain hardcoded hex colors,
 * font sizes, or styling constants. Instead they import from this module
 * (via design/index.ts) and pass tokens to chart blocks.
 *
 * Change a value here → every widget updates automatically.
 */

import { CHART_SERIES_COLORS } from './theme';
import type { ThemeMode } from '../../../types';
import type { AxisLabelFormatterParams } from './formatters';

// ---------------------------------------------------------------------------
// Canonical default color palette (superset of CHART_SERIES_COLORS)
// ---------------------------------------------------------------------------

/** 8-color default palette used by chart blocks and builders */
export const DEFAULT_COLORS: string[] = [
  ...CHART_SERIES_COLORS,
  '#ec4899', // pink-500
  '#06b6d4', // cyan-500
];

// ---------------------------------------------------------------------------
// AG-Charts theme tokens
// ---------------------------------------------------------------------------

const FONT_FAMILY = 'Inter, system-ui, sans-serif';

export const AG_CHART_TOKENS = {
  font: {
    family: FONT_FAMILY,
  },
  title: {
    fontSize: 14,
    fontWeight: 600 as const,
    color: '#374151',       // neutral-700
    fontFamily: FONT_FAMILY,
  },
  axis: {
    label: {
      color: '#6b7280',     // neutral-500
      fontSize: 11,
      fontFamily: FONT_FAMILY,
      autoRotate: false,
      autoRotateAngle: 335,
    },
    line: { color: '#e5e7eb' },   // neutral-200
    tick: { color: '#e5e7eb' },
    gridLine: {
      style: [{ stroke: '#f3f4f6', lineDash: [4, 4] }] as Array<{ stroke: string; lineDash: number[] }>,
    },
  },
} as const;

// ---------------------------------------------------------------------------
// Per-chart-type series defaults
// ---------------------------------------------------------------------------

export const SERIES_DEFAULTS = {
  bar: {
    cornerRadius: 4,
  },
  line: {
    strokeWidth: 2,
    marker: {
      size: 6,
      strokeWidth: 2,
      stroke: '#ffffff',
    },
  },
  area: {
    fillOpacity: 0.15,
    strokeWidth: 2,
    marker: {
      size: 5,
    },
  },
  pie: {
    strokeWidth: 2,
    pieStrokeColor: '#ffffff',
  },
} as const;

// ---------------------------------------------------------------------------
// Builder functions
// ---------------------------------------------------------------------------

// Theme-specific tokens. DARK values are the chart design system verbatim
// ("Institutional Research Terminal" sheet); LIGHT derives best-effort
// equivalents from the app's neutral scale — the DS is dark-first and so are
// customer demos.
const LIGHT_TOKENS = {
  title: { color: '#374151' },       // neutral-700
  label: { color: '#6b7280' },       // neutral-500
  line: { color: '#e5e7eb' },        // neutral-200
  tick: { color: '#e5e7eb' },
  gridLine: { stroke: 'rgba(212, 212, 216, 0.5)' },  // neutral-300 @50%
  zeroLine: { stroke: '#a1a1aa' },   // neutral-400
  card: { fill: '#ffffff', stroke: '#e2e8f0' },
  tooltip: { fill: '#ffffff', stroke: '#e2e8f0', text: '#171717', subtle: '#475569' },
  crosshair: { stroke: '#94a3b8' },
};

const DARK_TOKENS = {
  title: { color: '#e5e7eb' },
  label: { color: '#8C99B2' },       // DS axis-label gray
  line: { color: '#2E3647' },
  tick: { color: '#2E3647' },
  gridLine: { stroke: 'rgba(46, 54, 71, 0.5)' },     // DS: 0.5px #2E3647 @50%
  zeroLine: { stroke: '#404759' },   // DS zero line
  card: { fill: '#1C212E', stroke: '#2E3647' },
  tooltip: { fill: '#1E2535', stroke: '#2E3647', text: '#ffffff', subtle: '#B2BFD9' },
  crosshair: { stroke: '#404759' },
};

/** Chart-DS chrome constants shared by chart components. */
export const CHART_DS = {
  legend: { dotSize: 7, labelSize: 10 },
  gridStrokeWidth: 0.5,
  monoStack: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
} as const;

export function getThemeTokens(mode: ThemeMode) {
  return mode === 'dark' ? DARK_TOKENS : LIGHT_TOKENS;
}

/** Build a complete AG-Charts theme object from a color palette and theme mode */
export function buildAgChartTheme(
  colors: string[],
  mode: ThemeMode = 'light',
  opts?: {
    /** Does the chart have a Y axis on the left side? */
    hasLeftAxis?: boolean;
    /** Does the chart have a Y axis on the right side? */
    hasRightAxis?: boolean;
    /** Does the chart show end-value tags? */
    hasEndValueTags?: boolean;
  },
) {
  const t = getThemeTokens(mode);
  // Dynamic padding based on axis layout
  const leftPad = opts?.hasLeftAxis ? 4 : 20;   // No left axis → pad for first X label
  const rightPad = opts?.hasRightAxis ? (opts?.hasEndValueTags ? 12 : 4) : 20; // No right axis → pad for last X label

  return {
    baseTheme: mode === 'dark' ? 'ag-default-dark' as const : 'ag-default' as const,
    palette: {
      fills: [...colors],
      strokes: [...colors],
    },
    // Typed tooltip styling hooks (tooltip* params ONLY — chrome* params also
    // tint menus/toolbars). Border COLOR comes from the
    // --ag-charts-tooltip-border CSS var in index.css.
    params: {
      tooltipBackgroundColor: t.tooltip.fill,
      tooltipBorder: true,
      tooltipTextColor: t.tooltip.text,
      tooltipSubtleTextColor: t.tooltip.subtle,
      popupShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
    },
    overrides: {
      common: {
        title: {
          fontSize: AG_CHART_TOKENS.title.fontSize,
          fontWeight: AG_CHART_TOKENS.title.fontWeight,
          fontFamily: AG_CHART_TOKENS.title.fontFamily,
          color: t.title.color,
        },
        padding: { top: 12, right: rightPad + 4, bottom: 6, left: leftPad + 4 },
        legend: {
          spacing: 4,
          item: { paddingX: 8, paddingY: 4 },
        },
        // DS axis language: 10px labels, NO tick marks, hairline gridlines.
        // (v13 rejects overrides.cartesian — axis theme defaults live under
        // common.axes — the old block was silently ignored at runtime.)
        axes: {
          category: {
            label: {
              ...AG_CHART_TOKENS.axis.label,
              color: t.label.color,
            },
            line: { stroke: t.line.color },
            tick: { enabled: false },
          },
          number: {
            label: {
              fontSize: AG_CHART_TOKENS.axis.label.fontSize,
              fontFamily: AG_CHART_TOKENS.axis.label.fontFamily,
              color: t.label.color,
              padding: 2,
            },
            line: { stroke: t.line.color },
            tick: { enabled: false },
            gridLine: {
              style: [{ stroke: t.gridLine.stroke, strokeWidth: CHART_DS.gridStrokeWidth }],
            },
          },
        },
      },
    },
  };
}

/** Build standard cartesian axes (dictionary format for AG-Charts) */
export function buildCartesianAxes(overrides?: {
  xLabelFormatter?: (params: AxisLabelFormatterParams) => string;
  /** Explicit category tick values (must exactly match the data's x values).
   *  Also enables dashed x gridlines at those ticks. */
  xTickValues?: Array<string | number>;
  /** Axis titles (e.g. "Date" / "Price ($)"). */
  xTitle?: string;
  yTitle?: string;
  yLabelFormatter?: (params: AxisLabelFormatterParams) => string;
  yPosition?: 'left' | 'right';
  xGridLine?: object;
  yGridLine?: object;
  /** Lock Y axis range so it doesn't rescale when series are hidden */
  fixedRange?: { min: number; max: number };
}) {
  return {
    x: {
      type: 'category' as const,
      position: 'bottom' as const,
      label: {
        autoRotate: false,
        autoRotateAngle: AG_CHART_TOKENS.axis.label.autoRotateAngle,
        ...(overrides?.xLabelFormatter ? { formatter: overrides.xLabelFormatter } : {}),
      },
      ...(overrides?.xTitle ? { title: { enabled: true, text: overrides.xTitle, fontSize: 11 } } : {}),
      ...(overrides?.xTickValues ? { interval: { values: overrides.xTickValues } } : {}),
      // Grid/tick/line styling inherits from the theme (overrides.common.axes)
      // unless explicitly overridden — keeps this builder theme-mode agnostic.
      ...(overrides?.xGridLine
        ? { gridLine: overrides.xGridLine }
        : overrides?.xTickValues
          ? { gridLine: { enabled: true, style: [{ stroke: AG_CHART_TOKENS.axis.gridLine.style[0].stroke, strokeWidth: CHART_DS.gridStrokeWidth, lineDash: [4, 4] }] } }
          : {}),
    },
    y: {
      type: 'number' as const,
      position: (overrides?.yPosition ?? 'right') as 'left' | 'right',
      ...(overrides?.yGridLine ? { gridLine: overrides.yGridLine } : {}),
      ...(overrides?.yTitle ? { title: { enabled: true, text: overrides.yTitle, fontSize: 11 } } : {}),
      ...(overrides?.yLabelFormatter ? { label: { formatter: overrides.yLabelFormatter } } : {}),
      ...(overrides?.fixedRange
        ? {
            min: overrides.fixedRange.min,
            max: overrides.fixedRange.max,
            // DS gridline cap: fixed range + matching nice step pins the count.
            interval: { step: niceStep((overrides.fixedRange.max - overrides.fixedRange.min) / 5) },
          }
        : {}),
      // A fixed thickness leaves no room for a rotated title — let AG
      // auto-size the axis when one is present.
      ...(overrides?.yTitle ? {} : { thickness: 40 }),
    },
  };
}

// ---------------------------------------------------------------------------
// Nice range computation (matches AG-Charts axis scaling)
// ---------------------------------------------------------------------------

function niceStep(rawStep: number): number {
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  if (norm <= 1) return mag;
  if (norm <= 2) return 2 * mag;
  if (norm <= 5) return 5 * mag;
  return 10 * mag;
}

/** Enumerate explicit tick values across [min, max] at `step`. AG-Charts only
 *  reliably ticks a secondary (right) number axis when given explicit
 *  interval.values — interval.step there collapses to the single zero tick. */
function tickValues(min: number, max: number, step: number): number[] | undefined {
  if (!(step > 0) || !Number.isFinite(min) || !Number.isFinite(max)) return undefined;
  const out: number[] = [];
  const start = Math.ceil(min / step - 1e-9) * step;
  for (let v = start; v <= max + step * 1e-9 && out.length < 200; v += step) {
    out.push(Number(v.toFixed(6)));
  }
  return out;
}

/** Compute a nice axis range — rounds min/max to clean tick boundaries */
export function computeNiceRange(dataMin: number, dataMax: number): { min: number; max: number } {
  if (dataMin === dataMax) return { min: dataMin - 1, max: dataMax + 1 };
  const range = dataMax - dataMin;
  const paddedMin = dataMin - range * 0.05;
  const paddedMax = dataMax + range * 0.05;
  const step = niceStep((paddedMax - paddedMin) / 5);
  return {
    min: Math.floor(paddedMin / step) * step,
    max: Math.ceil(paddedMax / step) * step,
  };
}

/** Compute a fixed Y range from all values of the given keys across all data rows */
export function computeFixedRange(
  data: Record<string, unknown>[],
  yKeys: string[],
  stacked?: boolean,
): { min: number; max: number } {
  const values: number[] = [];
  if (stacked) {
    for (const row of data) {
      let cum = 0;
      for (const key of yKeys) {
        const v = row[key];
        if (typeof v === 'number') cum += v;
      }
      values.push(cum);
      values.push(0);
    }
  } else {
    for (const key of yKeys) {
      for (const row of data) {
        const v = row[key];
        if (typeof v === 'number') values.push(v);
      }
    }
  }
  if (values.length === 0) return { min: 0, max: 1 };
  return computeNiceRange(Math.min(...values), Math.max(...values));
}

// ---------------------------------------------------------------------------
// End-value axis tags (crossLines with invisible line + colored label)
// ---------------------------------------------------------------------------

interface EndValueSeriesDef {
  yKey: string;
  yName: string;
  color?: string;
}

/** Build crossLine configs to show end-value tags on the Y axis.
 *  When stacked=true, crossLine values are cumulative (matching visual stack positions).
 *  Labels that would overlap are nudged apart. */
export function buildEndValueTags(
  data: Record<string, unknown>[],
  series: EndValueSeriesDef[],
  colors: string[],
  formatter?: (value: number) => string,
  labelPosition: 'left' | 'right' = 'right',
  stacked?: boolean,
): Record<string, unknown>[] {
  if (data.length === 0) return [];
  const lastRow = data[data.length - 1];

  // First pass: compute raw crossLine values
  const entries: Array<{ color: string; text: string; value: number }> = [];
  let cumulative = 0;

  for (let i = 0; i < series.length; i++) {
    const s = series[i];
    const rawValue = lastRow[s.yKey];
    if (rawValue == null || typeof rawValue !== 'number') continue;
    const color = s.color || colors[i % colors.length];
    const text = formatter ? formatter(rawValue) : rawValue.toLocaleString();

    let crossLineValue: number;
    if (stacked) {
      cumulative += rawValue;
      crossLineValue = cumulative;
    } else {
      crossLineValue = rawValue;
    }

    entries.push({ color, text, value: crossLineValue });
  }

  if (entries.length === 0) return [];

  // Second pass: nudge overlapping labels apart
  // Sort by value, then push apart any that are too close
  entries.sort((a, b) => a.value - b.value);
  const allValues = entries.map((e) => e.value);
  const range = Math.max(...allValues) - Math.min(...allValues) || 1;
  const minGap = range * 0.08; // ~8% of data range as minimum gap between labels

  // Multiple passes to resolve cascading overlaps
  for (let pass = 0; pass < 5; pass++) {
    let changed = false;
    for (let i = 1; i < entries.length; i++) {
      const gap = entries[i].value - entries[i - 1].value;
      if (gap < minGap) {
        const deficit = minGap - gap;
        entries[i - 1].value -= deficit / 2;
        entries[i].value += deficit / 2;
        changed = true;
      }
    }
    if (!changed) break;
  }

  // Render the tag INSIDE the plot at the line's endpoint rather than out in the
  // axis margin, so it doesn't collide with the axis tick labels (which live on
  // the same side). 'inside-right'/'inside-left' keeps the colored end value next
  // to where the series ends while the gray axis ticks stay clear on the outside.
  const insidePosition = labelPosition === 'left' ? 'inside-left' : 'inside-right';
  return entries.map((e) => ({
    type: 'line',
    value: e.value,
    strokeWidth: 0,
    strokeOpacity: 0,
    label: {
      text: e.text,
      fontSize: 9,
      fontWeight: 700,
      fontFamily: FONT_FAMILY,
      color: e.color,
      padding: 2,
      position: insidePosition,
    },
  }));
}

// ---------------------------------------------------------------------------
// Legend config with optional last values
// ---------------------------------------------------------------------------

/** Build legend config, optionally appending last values to series names */
export function buildLegendConfig(opts: {
  enabled: boolean;
  position?: 'top' | 'bottom' | 'left' | 'right';
  toggleSeries?: boolean;
  legendValues?: boolean;
  data?: Record<string, unknown>[];
  series?: Array<{ yKey: string; yName: string }>;
  valueFormatter?: (value: number) => string;
}) {
  const legend: Record<string, unknown> = {
    enabled: opts.enabled,
    position: opts.position ?? 'bottom',
    toggleSeries: opts.toggleSeries ?? true,
    spacing: 4,
    item: { paddingX: 8, paddingY: 4 },
  };

  if (opts.legendValues && opts.data?.length && opts.series?.length) {
    const lastRow = opts.data[opts.data.length - 1];
    const fmt = opts.valueFormatter || ((v: number) => v.toLocaleString());

    // AG-Charts legend label formatter receives { itemId, seriesId, value (text) }
    legend.item = {
      ...legend.item as object,
      label: {
        formatter: (params: { value: string }) => {
          // Find the series matching this legend label
          const match = opts.series!.find((s) => s.yName === params.value);
          if (!match) return params.value;
          const val = lastRow[match.yKey];
          if (val == null || typeof val !== 'number') return params.value;
          return `${params.value}  ${fmt(val)}`;
        },
      },
    };
  }

  return legend;
}

// ---------------------------------------------------------------------------
// Axis layout — auto-assign positions from axis IDs
// ---------------------------------------------------------------------------

export interface AxisLayout {
  /** Axis ID → dictionary key used in AG-Charts axes config */
  key: string;
  /** Which side this axis renders on */
  position: 'left' | 'right';
  /** Whether this axis shows grid lines (only the first axis does) */
  showGrid: boolean;
}

/**
 * Compute axis positions from a list of unique axis IDs.
 * Rules:
 *   1 axis  → right
 *   2 axes  → 1st left, 2nd right
 *   3+ axes → 1st left, rest on right
 */
export function computeAxisLayout(axisIds: string[]): Map<string, AxisLayout> {
  const unique = Array.from(new Set(axisIds));
  const map = new Map<string, AxisLayout>();

  if (unique.length <= 1) {
    const id = unique[0] || 'a';
    map.set(id, { key: 'y', position: 'right', showGrid: true });
  } else {
    unique.forEach((id, i) => {
      if (i === 0) {
        map.set(id, { key: 'y', position: 'left', showGrid: true });
      } else {
        map.set(id, { key: `y${i}`, position: 'right', showGrid: false });
      }
    });
  }

  return map;
}

/** Build dictionary-style AG-Charts axes from axis layout + config */
export function buildMultiAxes(opts: {
  layout: Map<string, AxisLayout>;
  mode: ThemeMode;
  xLabelFormatter?: (params: AxisLabelFormatterParams) => string;
  /** Explicit category tick values (must exactly match the data's x values).
   *  Also enables dashed x gridlines at those ticks. */
  xTickValues?: Array<string | number>;
  /** Axis titles: x plus per-axis-id y titles. */
  xTitle?: string;
  yTitles?: Record<string, string>;
  yLabelFormatters?: Record<string, (params: AxisLabelFormatterParams) => string>;
  /** Lock Y axis ranges per axis ID so they don't rescale when series are hidden */
  fixedRanges?: Record<string, { min: number; max: number }>;
  /** DS: dual-axis labels color-coded to their series (per axis ID). */
  axisColors?: Record<string, string>;
  /** Axis IDs that get a zero baseline crossLine (DS zero-line rule). */
  zeroLineAxes?: string[];
  /** Target tick-interval count per axis ID (higher = more labels). Defaults
   *  to the DS cap (~5). Useful for a gridline-less secondary axis whose nice
   *  range runs wider than its data, leaving the default step too sparse. */
  yTickCounts?: Record<string, number>;
  /** Secondary axis IDs whose zero should align with the primary axis's zero.
   *  The axis keeps its own (tighter) scale but its range is anchored so 0 sits
   *  at the same vertical fraction as the primary — e.g. a rebased % chart where
   *  every series starts at 0, so all baselines must coincide. */
  alignZeroAxes?: string[];
}) {
  const t = getThemeTokens(opts.mode);
  // Primary axis (first in layout) supplies the zero fraction for any axis
  // opting into alignZeroAxes.
  const primaryId = Array.from(opts.layout.keys())[0];
  const primaryRange = primaryId ? opts.fixedRanges?.[primaryId] : undefined;

  const axes: Record<string, unknown> = {
    x: {
      type: 'category' as const,
      position: 'bottom' as const,
      label: {
        autoRotate: false,
        autoRotateAngle: AG_CHART_TOKENS.axis.label.autoRotateAngle,
        ...(opts.xLabelFormatter ? { formatter: opts.xLabelFormatter } : {}),
      },
      tick: { enabled: false },
      ...(opts.xTitle ? { title: { enabled: true, text: opts.xTitle, fontSize: 11, color: t.label.color } } : {}),
      ...(opts.xTickValues
        ? {
            interval: { values: opts.xTickValues },
            gridLine: {
              enabled: true,
              style: [{ stroke: t.gridLine.stroke, strokeWidth: CHART_DS.gridStrokeWidth, lineDash: [4, 4] }],
            },
          }
        : {}),
    },
  };

  opts.layout.forEach((layout, axisId) => {
    const formatter = opts.yLabelFormatters?.[axisId];
    const yTitle = opts.yTitles?.[axisId];
    const axisColor = opts.axisColors?.[axisId];
    // Effective range: usually the locked fixedRange, but an alignZeroAxes axis
    // is re-anchored so its zero sits at the same fraction as the primary's —
    // the tightest range that contains the data with that zero position. Bounds
    // need not be "nice"; the explicit tick values below land on nice multiples.
    let fixedRange = opts.fixedRanges?.[axisId];
    if (
      fixedRange && primaryRange && axisId !== primaryId
      && opts.alignZeroAxes?.includes(axisId)
    ) {
      const span = primaryRange.max - primaryRange.min;
      const pBelow = span > 0 ? -primaryRange.min / span : 0; // fraction below zero
      if (pBelow > 0 && pBelow < 1) {
        const rangeR = Math.max(fixedRange.max / (1 - pBelow), -fixedRange.min / pBelow);
        fixedRange = { min: -pBelow * rangeR, max: (1 - pBelow) * rangeR };
      }
    }
    // DS gridline cap (~5-7 lines): a fixed range plus the matching nice step
    // pins the count deterministically. yTickCounts opts an axis into a denser
    // cap (e.g. a gridline-less spread axis wanting more % labels). Passed as
    // explicit interval.values, not a step — AG only ticks a right-side
    // secondary axis reliably with values (step collapses it to one label).
    const targetCount = opts.yTickCounts?.[axisId] ?? 5;
    const values = fixedRange
      ? tickValues(fixedRange.min, fixedRange.max, niceStep((fixedRange.max - fixedRange.min) / targetCount))
      : undefined;
    const interval = values && values.length ? { interval: { values } } : {};
    const zeroLine = opts.zeroLineAxes?.includes(axisId)
      ? { crossLines: [{ type: 'line', value: 0, stroke: t.zeroLine.stroke, strokeWidth: 1 }] }
      : {};
    axes[layout.key] = {
      type: 'number' as const,
      position: layout.position,
      ...(yTitle ? { title: { enabled: true, text: yTitle, fontSize: 11, color: axisColor ?? t.label.color } } : {}),
      label: {
        color: axisColor ?? t.label.color,
        fontSize: AG_CHART_TOKENS.axis.label.fontSize,
        fontFamily: AG_CHART_TOKENS.axis.label.fontFamily,
        padding: 2,
        ...(formatter ? { formatter } : {}),
      },
      line: { stroke: t.line.color },
      tick: { enabled: false },
      gridLine: layout.showGrid
        ? { style: [{ stroke: t.gridLine.stroke, strokeWidth: CHART_DS.gridStrokeWidth }] }
        : { style: [{ stroke: 'transparent' }] },
      ...(fixedRange ? { min: fixedRange.min, max: fixedRange.max } : {}),
      ...interval,
      ...zeroLine,
      // A fixed thickness leaves no room for a rotated title — let AG
      // auto-size the axis when one is present.
      ...(yTitle ? {} : { thickness: 40 }),
    };
  });

  return axes;
}

/** Get series styling defaults for a given chart type */
export function getSeriesDefaults(chartType: 'bar' | 'line' | 'area' | 'pie' | 'donut') {
  switch (chartType) {
    case 'bar':
      return { ...SERIES_DEFAULTS.bar };
    case 'line':
      return {
        strokeWidth: SERIES_DEFAULTS.line.strokeWidth,
        marker: { ...SERIES_DEFAULTS.line.marker },
      };
    case 'area':
      return {
        fillOpacity: SERIES_DEFAULTS.area.fillOpacity,
        strokeWidth: SERIES_DEFAULTS.area.strokeWidth,
        marker: { ...SERIES_DEFAULTS.area.marker },
      };
    case 'pie':
    case 'donut':
      return {
        strokeWidth: SERIES_DEFAULTS.pie.strokeWidth,
      };
    default:
      return { ...SERIES_DEFAULTS.bar };
  }
}
